import { useState } from 'react';
import { Stack, Group, Button, NumberInput, TextInput, Text, Alert } from '@mantine/core';
import { IconLink, IconFilter } from '@tabler/icons-react';
import { RepositorySelect } from '@ayocore/platform/client/components/RepositorySelect/RepositorySelect';
import { useDealRepository } from '@ayocore/erp/crm/client/rpc';
import { useAccountingDocumentTransactionRepository } from '@ayocore/erp/accounting/client/rpc';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import DealPickerModal from '@ayocore/erp/crm/client/components/Deals/DealPickerModal';
import type { Deal } from '@ayocore/erp/crm/schemas/deal.schema';
import logger from '@ayocore/platform/common/helpers/logger';

export interface LinkDealFormProps extends Record<string, unknown> {
  documentId: number;
  /** Полная сумма документа */
  documentAmount: number | null;
  /** Уже привязанная сумма */
  linkedAmount: number | null;
  /** Комментарий из документа (подставляется в поле) */
  documentDescription?: string | null;
  /** Вызывается после успешной привязки */
  onLinked?: () => void;
}

/**
 * Форма привязки документа поставщика к сделке.
 * Создаёт запись в accounting_document_transactions.
 */
export function LinkDealForm({
  documentId,
  documentAmount,
  linkedAmount,
  documentDescription,
  onLinked,
}: LinkDealFormProps) {
  const dealRepository = useDealRepository();
  const transactionRepository = useAccountingDocumentTransactionRepository();
  const dealPicker = useModal(DealPickerModal);

  const [dealId, setDealId] = useState<number | null>(null);
  const [dealLabel, setDealLabel] = useState<string>('');
  const [amount, setAmount] = useState<number | string>(() => {
    const total = Number(documentAmount) || 0;
    const linked = Number(linkedAmount) || 0;
    const rest = total - linked;
    return rest > 0 ? rest : total;
  });
  const [description, setDescription] = useState(documentDescription?.trim() ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!dealId) {
      setError('Выберите сделку');
      return;
    }
    if (!amount || Number(amount) === 0) {
      setError('Укажите сумму');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await transactionRepository.create({
        document_id: documentId,
        deal_id: dealId,
        amount: Number(amount),
        operation_date: new Date().toISOString(),
        description: description.trim() || null,
      });
      setSuccess(true);
      setTimeout(() => onLinked?.(), 600);
    } catch (err) {
      logger.error('Ошибка привязки сделки:', err);
      setError(err instanceof Error ? err.message : 'Не удалось привязать сделку');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Alert color='green' title='Готово'>
        Сделка привязана к документу.
      </Alert>
    );
  }

  return (
    <Stack gap='sm'>
      {error && (
        <Alert color='red' title='Ошибка' withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Group gap='xs' align='flex-end'>
        <RepositorySelect
          label='Сделка'
          size='xs'
          style={{ flex: 1 }}
          repo={dealRepository.withRelations().orderBy('id', 'desc')}
          displayField='number'
          placeholder='Поиск по номеру сделки...'
          enableServerSearch
          searchDebounce={300}
          minSearchLength={1}
          onChange={value => {
            setDealId(value ? parseInt(value, 10) : null);
            setDealLabel('');
          }}
          value={dealId?.toString() ?? ''}
        />
        <Button
          size='xs'
          variant='light'
          leftSection={<IconFilter size={14} />}
          onClick={() => {
            dealPicker.open(
              {
                title: 'Выбрать сделку',
                selectionMode: 'single' as const,
                onDealSelected: (deals: Deal[]) => {
                  if (deals.length > 0) {
                    setDealId(deals[0].id as number);
                    setDealLabel(deals[0].number || `#${deals[0].id}`);
                  }
                  dealPicker.close();
                },
                onClose: () => dealPicker.close(),
              },
              { size: '90%' }
            );
          }}
        >
          Фильтр
        </Button>
      </Group>
      {dealLabel && (
        <Text size='xs' c='blue'>
          Выбрана: {dealLabel}
        </Text>
      )}

      <NumberInput
        label='Сумма'
        size='xs'
        value={amount}
        onChange={val => setAmount(val)}
        allowNegative
        allowDecimal
        decimalScale={2}
        decimalSeparator=','
        thousandSeparator=' '
      />

      {documentAmount != null && (
        <Text size='xs' c='dimmed'>
          Сумма документа: {new Intl.NumberFormat('ru-RU').format(Number(documentAmount))} ₽
          {linkedAmount
            ? `, привязано: ${new Intl.NumberFormat('ru-RU').format(Number(linkedAmount))} ₽`
            : ''}
        </Text>
      )}

      <TextInput
        label='Описание'
        size='xs'
        placeholder='Необязательно'
        value={description}
        onChange={e => setDescription(e.currentTarget.value)}
      />

      <Group justify='flex-end'>
        <Button
          size='xs'
          leftSection={<IconLink size={14} />}
          onClick={handleSubmit}
          loading={loading}
          disabled={!dealId}
        >
          Привязать
        </Button>
      </Group>
    </Stack>
  );
}
